import React, { useMemo, useState, useEffect } from "react";
import "./recharge-theme.css";
import AudioAnswer from "./AudioAnswer";
import FairmentIntroCardB from "./FairmentIntroCardB";
import { RankingQuestion } from "./components/RankingQuestion";
import ChapterNavigation from "./components/ChapterNavigation";
import { submitSurveyWithValidation, submitPartialSurvey } from "./services/submitSurvey";
import logoSrc from "./assets/fairment-logo-white.png";
import superMikrobenImg from "./assets/fairment-SuperMikroben.webp";
import praezisiotikumImg from "./assets/fairment-Präzisiotikum.webp";
import superFloraImg from "./assets/fairment-SuperFlora.webp";
import { v4 as uuidv4 } from 'uuid';
import SuccessScreen from './components/SuccessScreen';
import ScreenoutScreen from './components/ScreenoutScreen';
import QuotaFullScreen from './components/QuotaFullScreen';
import { registerVisitor, markVisitorScreenedOut, checkQuota } from './services/visitorService';
import { initializePanelTracking, redirectToPanel } from './services/panelService';
import { useIsMobile } from './hooks/useIsMobile';
// Hilfsfunktion zum Mischen von Arrays
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
export default function FairmentFragebogen({ fragebogenType = 'B' }) {
  //Fragenkatalog ----------
  const chapters = useMemo(() => ([
    // 1 DEMOGRAFIE
    {
      title: "1. DEMOGRAFIE: Wir möchten dich kennenlernen",
      questions: [
        { key: "1.1", type: "radio", text: "1.1 Was beschreibt deinen aktuellen Gesundheitszustand am besten?", options: ["Krank","Unwohl","Weder besonders gesund, noch krank","Gesund"], required: true },
        { key: "1.2", type: "radio", text: "1.2 Welchem Geschlecht ordnest du dich zu?", options: ["Weiblich", "Männlich", "Divers", "Möchte ich nicht angeben"], required: true },
        { key: "1.3", type: "radio", text: "1.3 Wie alt bist du?", options: ["18 – 24", "25 – 29", "30 – 34", "35 – 44", "45 – 55", "56 – 64", "65+"], required: true },
        { key: "1.4", type: "radio", text: "1.4 Welche Beschreibung trifft am besten auf deine aktuelle Lebenssituation zu?", options: [
          "Berufstätig",
          "Elternteil mit Kind oder Kindern zuhause",
          "Student",
          "Rentner/Pensionär",
          "Arbeitssuchend",
          "Sonstiges:"
        ] },
        { key: "1.5", type: "radio", text: "1.5 Wie beschreibst du deinen gesundheitlichen Lebensstil (Bitte kreuze die Angabe an, die am besten zu dir passt)", options: [
          "Gesundheit spielt in meinem Alltag kaum eine Rolle – ich achte selten auf Ernährung, Bewegung, Schlaf oder Vorsorge.",
          "Ich berücksichtige Gesundheit gelegentlich, setze es aber unregelmäßig um; andere Dinge haben oft Vorrang.",
          "Ich versuche, auf meine Gesundheit zu achten, schaffe es jedoch nur teilweise; Ernährung, Bewegung und Vorsorge sind wechselhaft.",
          "Ich lebe überwiegend gesundheitsbewusst; ich bewege mich regelmäßig, ernähre mich meist ausgewogen und nehme Vorsorgetermine meist wahr.",
          "Gesundheit ist fest in meinen Alltag integriert; ich bewege mich regelmäßig, ernähre mich konsequent ausgewogen, schlafe ausreichend und gehe planmäßig zur Vorsorge."
        ] },
        { key: "1.6", type: "select", text: "1.6 In welchem Bundesland wohnen Sie derzeit?", options: [
          "Baden-Württemberg",
          "Bayern",
          "Berlin",
          "Brandenburg",
          "Bremen",
          "Hamburg",
          "Hessen",
          "Mecklenburg-Vorpommern",
          "Niedersachsen",
          "Nordrhein-Westfalen",
          "Rheinland-Pfalz",
          "Saarland",
          "Sachsen",
          "Sachsen-Anhalt",
          "Schleswig-Holstein",
          "Thüringen"
        ], required: true },
        { key: "1.7", type: "text", text: "1.7 Bitte geben Sie zusätzlich Ihre Postleitzahl an (freiwillig):" },
        { key: "1.8", type: "radio", text: "1.8 Welchen höchsten Bildungsabschluss haben Sie erreicht?", options: [
          "Kein Schulabschluss",
          "Hauptschulabschluss / Volksschule",
          "Realschulabschluss / Mittlere Reife",
          "Fachhochschulreife (Fachabitur)",
          "Allgemeine Hochschulreife (Abitur)",
          "Berufsausbildung",
          "Bachelorabschluss",
          "Masterabschluss",
          "Promotion / Doktortitel"
        ], required: true, screenout: ["Kein Schulabschluss", "Hauptschulabschluss / Volksschule"] },
        { key: "1.9", type: "radio", text: "1.9 Wie hoch ist Ihr monatliches Haushaltsnettoeinkommen, also das gesamte zur Verfügung stehende Einkommen aller im Haushalt lebenden Mitglieder nach Abzug von Steuern und Sozialversicherung? Dazu zählt z.B. auch Bafög, Kindergeld, etc.", options: [
          "Weniger als 1.000,00 €",
          "1.000,00 € - 1.999,00 €",
          "2.000,00 € - 2.999,00 €",
          "3.000,00 € - 3.999,00 €",
          "4.000,00 € - 4.999,00 €",
          "5.000€ und mehr",
          "Keine Angabe"
        ], required: true, screenout: ["Weniger als 1.000,00 €", "1.000,00 € - 1.999,00 €", "2.000,00 € - 2.999,00 €", "Keine Angabe"] },
        { key: "1.10", type: "radio", text: "1.10 Wie häufig nutzen Sie Nahrungsergänzungsmittel?", options: [
          "Täglich",
          "Mehrmals pro Woche",
          "Gelegentlich (z. B. 1–2 Mal im Monat)",
          "Selten (weniger als einmal im Monat)",
          "Nie"
        ], required: true, screenout: ["Selten (weniger als einmal im Monat)", "Nie"] }
      ]
    },

    // 2 GESUNDHEITSSTATUS & ROUTINEN
    {
      title: "2. GESUNDHEITSSTATUS & ROUTINEN: Dein aktueller Gesundheitsstatus",
      questions: [
        { key: "2.1", type: "matrix", text: "2.1 Welche festen Routinen hast du, um deine Gesundheit und dein Wohlbefinden zu stärken? (Mehrfachauswahl, bitte ankreuzen, ob täglich, wöchentlich, monatlich, nie)", rows: [
          "Sport / Bewegung (z. B. Fitness, Yoga, Spazieren)",
          "Entspannungs- oder Achtsamkeitsübungen",
          "Gesunde Mahlzeiten",
          "Ausreichend Wasser trinken",
          "Schlafroutine (z. B. feste Zubettgehzeit)",
          "Sauna, Wellness oder Spa",
          "Naturaufenthalte (Wandern, Garten, frische Luft)",
          "Geräte wie z. B. Smart Watch, Computer oder Handy, um Gesundheit zu dokumentieren",
          "Andere:"
], cols: ["Täglich", "Mehrmals pro Woche", "Mehrmals im Monat", "Mehrmals im Jahr", "Nie"] },
        { key: "2.2", type: "checkbox", text: "2.2 Wie regelmäßig nimmst du aktuell Nahrungsergänzungsmittel ein? (Mehrfachauswahl möglich)", options: [
    "Gar nicht",
"Nur bei akuten Problemen",
"Unregelmäßig",
"Gelegentlich",
"Täglich im Rahmen einer Kur",
"Täglich das ganze Jahr hindurch"
        ] },
     { key: "2.3", type: "scale", text: "2.3 Welche der folgenden Beschwerden treffen auf dich zu? (1 = sehr gering, 5 = sehr stark)", rows: [
  "Verdauungsprobleme – Blähungen, Völlegefühl, unregelmäßiger Stuhlgang",
  "Immunsystem-Schwäche – Häufige Erkältungen, langsame Genesung",
  "Energiemangel – Chronische Müdigkeit, Antriebslosigkeit",
  "Hautprobleme – Unreine Haut, Ekzeme, Allergien",
  "Stimmungsschwankungen – Reizbarkeit, depressive Verstimmungen",
  "Gewichtsprobleme – Schwierigkeiten beim Abnehmen/Zunehmen",
  "Nahrungsmittelunverträglichkeiten – Reaktionen auf bestimmte Lebensmittel"
], min: 1, max: 5, leftLabel: "1 = sehr gering", rightLabel: "5 = sehr stark",
  checkboxRow: "Keine besonderen Beschwerden - Ich möchte präventiv etwas für meine Gesundheit tun" },
        { key: "2.4", type: "checkbox", text: "2.4 Was ist dein wichtigstes Ziel in Bezug auf deine Darmgesundheit? (Mehrfachauswahl möglich)", options: [
          "Magen-Darm Beschwerden lindern - Endlich wieder beschwerdefrei leben",
          "Immunsystem stärken - Seltener krank werden",
          "Mehr Energie bekommen - Mich wieder vital und leistungsfähig fühlen",
          "Gewicht regulieren - Mein Wohlfühlgewicht erreichen und halten",
          "Präventiv vorsorgen - Gesund bleiben und Krankheiten vorbeugen",
          "Hautbild verbessern - Reinere, gesündere Haut bekommen",
          "Allgemeines Wohlbefinden - Mich rundum wohler fühlen",
          "Leistung optimieren - Sport und Beruf noch besser bewältigen"
        ] },
{ key: "2.4a", type: "checkbox", text: "2.4a Wenn dein primäres Gesundheitsziel (oder deine primären Ziele) abgedeckt ist, wären zusätzlich andere Themen für dich interessant? (Maximal 2 Antworten möglich)", options: shuffleArray([
 "Frauengesundheit (z. B. Zyklus, Vaginalflora, Hormone)",
  "Beauty/Hautgesundheit/Glow (Strahlendes Hautbild, Anti-Aging von innen)",
  "Stress/Schlaf (Stressbewältigung, Schlafqualität verbessern)",
  "Nahrungsmittelunverträglichkeiten (Laktose, Gluten, Histamin)",
  "Darm-Hirn-Achse (Stimmung, mentale Klarheit, Psyche)",
  "Regeneration nach Antibiotika (Darmflora wiederaufbauen)",
  "Sporternährung/Performance (Muskelaufbau, Regeneration)"
]), showSelected24: true },
        { key: "2.4b", type: "radio", text: "2.4b  Wie schnell erwartest du erste Verbesserungen nach Start der Supplementeinnahme?", options: [
          "Sofort",
          "Kurzfristig – (innerhalb von 1-2 Wochen)",
          "Mittelfristig – (Innerhalb von 1-3 Monaten)"
        ] }
      ]
    },

    // 3 DEIN HAUPTWUNSCH
    {
    title: "3. DEIN HAUPTWUNSCH: Was beschäftigt dich?",
    subtitle: "Wir wissen, dass Gesundheit ein sensibles Thema ist. Gerade an dieser Stelle betonen wir wie wichtig uns der Datenschutz und die Anonymisierung sind, d.h. wir können keine Antworten bestimmten Teilnehmern zuordnen.",
    questions: [
      { key: "3.1", type: "textarea", text: "3.1 Beschreibe in deinen eigenen Worten dein größtes Problem oder deinen größten Wunsch in Bezug auf deine Gesundheit (auch über Darmgesundheit hinaus)? Uns interessiert, was du dir wünschst und – wenn es da etwas gibt -, was dich im Alltag belastet und wie du dich dabei fühlst." },
      { key: "3.2", type: "textarea", text: "3.2 Stell dir vor, du wachst morgen auf und dein Gesundheitswunsch ist erfüllt. Wie fühlt sich dieser Tag an? Was machst du anders, wie gehst du durch den Tag – und welches Gefühl begleitet dich dabei?" }
    ]
    },
    // 4 DARMGESUNDHEIT & MIKROBIOM-WISSEN
    {
      title: "4. DARMGESUNDHEIT & MIKROBIOM-WISSEN",
      questions: [
{ key: "4.1", type: "checkbox", text: "4.1 Welche Nahrungsergänzungsmittel oder andere Verfahren für die Darmgesundheit hast du bereits ausprobiert? (Mehrfachauswahl möglich)", options: [
  "Probiotika aus der Apotheke/Drogerie",
  "Präbiotika (Ballaststoffe)",
  "Fermentierte Lebensmittel (Sauerkraut, Kefir, Kombucha)",
  "Spezielle Mikrobiom-Nahrungsergänzung",
  "Darmreinigung/Detox-Kuren",
  "Ernährungsumstellung",
  "Stuhltests/Mikrobiom-Analyse",
  "Noch nichts davon",
  "Sonstiges:"
], hasTextField: true },
        { key: "4.2", type: "radio", text: "4.2 Wie beschreibst du deinen aktuellen Wissensstand über Darmgesundheit und das Mikrobiom?", options: [
          "Kompletter Neuling - Ich habe noch nie bewusst über meine Darmgesundheit nachgedacht",
          "Interessierter Einsteiger - Ich habe schon mal davon gehört, aber weiß wenig darüber",
          "Informierter Beginner - Ich weiß, dass es wichtig ist, aber bin unsicher bei der Umsetzung",
          "Gesundheitsbewusster - Ich achte bereits auf meine Darmgesundheit und probiere verschiedene Ansätze",
          "Erfahrener Anwender - Ich nutze bereits gezielt Probiotika und andere Mikrobiom-Produkte",
          "Experte - Ich kenne mich sehr gut aus und optimiere kontinuierlich mein Mikrobiom"
        ] },
{ key: "4.3", type: "scale", text: "4.3 Wie stehst du zu Produkten für die Darmgesundheit? (1 = lehne ab, 5 = stimme voll zu)", rows: [
  "Durch die regelmäßige Einnahme von Prä- oder Probiotika kann ich mein Immunsystem langfristig stärken.",
  "Prä-/Probiotika sorgen dafür, dass ich mich insgesamt fitter und energiegeladener fühle.",
  "Die Zusammensetzung meiner Darmbakterien beeinflusst meine allgemeine Gesundheit spürbar.",
  "Eine gesunde Darmflora dank Prä-/Probiotika senkt meiner Meinung nach das Risiko, krank zu werden.",
  "Die Wirkung von Prä- oder Probiotika geht über die Verdauung hinaus und hat weiteren Nutzen für meine Gesundheit."
], min: 1, max: 5, leftLabel: "1 = lehne ab", rightLabel: "5 = stimme voll zu" },
        { key: "4.4", type: "checkbox", text: "4.4 Was ist aktuell dein größtes Hindernis bei der Verbesserung deiner Darmgesundheit? (Mehrfachauswahl ist möglich)", options: [
          "Informationsüberfluss - Zu viele widersprüchliche Ratschläge",
          "Zeitmangel - Keine Zeit für komplizierte Routinen",
          "Unsicherheit - Ich weiß nicht, was wirklich wirkt",
          "Kosten - Nahrungsergänzung ist mir zu teuer",
          "Vergesslichkeit - Ich vergesse die regelmäßige Einnahme",
          "Skepsis - Ich zweifle an der Wirksamkeit von Probiotika",
          "Komplexität - Das Thema Mikrobiom ist mir zu kompliziert",
          "Bisherige Enttäuschungen - Andere Produkte haben nicht geholfen",
          "Ich sehe keine Hindernisse"
        ] }
      ]
    },

    // 5 MEDIEN
    {
      title: "5. MEDIEN",
      questions: [
        { key: "5.1", type: "radio", text: "5.1 Wie wichtig ist es für dich, dich regelmäßig über Gesundheitsthemen zu informieren?", options: [
"Ganz und gar unwichtig",
"Weniger wichtig",
"Neutral / teils-teils",
"Wichtig",
"Sehr wichtig"
        ] },
        { key: "5.2", type: "radio", text: "5.2 Schätze bitte wie viel deiner täglichen, aktiv gesuchten Informationen sich um das Thema Gesundheit drehen", options: [
          "0 % – Sehr wenig",
          "25 % – Eher wenig",
          "50 % – Mittel / teils-teils",
          "75 % – Eher viel",
          "100 % – Sehr viel"
        ] },
        { key: "5.3", type: "checkbox", text: "5.3 Wie informierst du dich am liebsten über Gesundheitsthemen? (Mehrfachauswahl ist möglich)", options: [
          "Artikel und Blogs",
          "Videos und YouTube",
          "Social Media",
          "Podcasts",
          "Bücher/Fachbücher",
          "Zeitschriften",
          "Persönliche Beratung - Gespräche mit Experten",
          "ChatGPT, Gemini oder andere KI",
          "Freundschaftlicher oder familiärer Rat - Gespräche mit Freunden oder Familie",
          "Newsletter - Regelmäßige E-Mail-Updates",
          "Ich informiere mich nicht aktiv über Gesundheitsthemen"
        ] }
      ]
    },

    // 6 PRODUKTPRÄFERENZEN & BUDGET
    {
      title: "6. PRODUKTPRÄFERENZEN & BUDGET",
      questions: [
        { key: "6.1", type: "radio", text: "6.1 Hast du in den letzten 12 Monaten Produkte für die Darmgesundheit gekauft?", options: ["Nein", "Ja"] },
        { key: "6.1a", type: "radio", showWhen: { key: "6.1", equals: "Ja" }, text: "6.1 a Wie oft kaufst du diese Produkte im Durchschnitt?", options: [
          "Etwa einmal im Monat",
          "Etwa einmal alle drei Monate",
          "Etwa einmal im halben Jahr",
          "Seltener als einmal im halben Jahr"
        ] },
        { key: "6.1b", type: "radio", showWhen: { key: "6.1", equals: "Ja" }, text: "6.1 b Wie regelmäßig nimmst du diese Produkte ein?", options: [
          "Täglich",
          "Mehrmals pro Woche",
          "Unregelmäßig (z. B. nur bei Beschwerden)",
          "In festen Kuren (z. B. 1–2 Monate, dann Pause)"
        ] },
        { key: "6.1c", type: "radio", showWhen: { key: "6.1", equals: "Ja" }, text: "6.1 c Kaufst du in der Regel …", options: [
          "Immer dasselbe Produkt",
          "Unterschiedliche Produkte desselben Anbieters",
          "Unterschiedliche Produkte verschiedener Anbieter"
        ] },
        { key: "6.1d", type: "radio", showWhen: { key: "6.1", equals: "Ja" }, text: "6.1 d Ab welchem Moment denkst du typischerweise über Nachkauf nach – unabhängig vom Anbieter?", options: [
          "Wenn noch ca. 10–14 Tage Vorrat übrig sind",
          "Erst wenn die Packung leer ist",
          "Wenn Beschwerden zurückkommen",
          "Ich plane fix (z. B. alle 30/45/60 Tage)"
        ] },
{ key: "6.1e", type: "checkbox", showWhen: { key: "6.1", equals: "Ja" }, text: "6.1 e Welche dieser Angebote würden dich am ehesten zu Wiederkauf oder Abo bewegen? (Maximal 3 Antworten möglich)", options: [
  "Flexibles Abo (pausieren/verschieben jederzeit)",
  "Bundle-Preisvorteil (z. B. −15 %)",
  "Gratis-Probiergröße eines ergänzenden Produkts",
  "Kurzer persönlicher E-Mail-Check-in (5–10 Min. Antwort)",
  "WhatsApp-Support für schnelle Fragen",
  "Reorder-Hinweis per E-Mail zum passenden Zeitpunkt",
  "Treuepunkte & Status-Level (z. B. Bronze/Silber/Gold)",
  "Exklusive Vorteile (z. B. früher Zugang zu neuen Produkten, Sonderrabatte)"
] },
{ key: "6.2", type: "ranking", text: "6.2 Bringe die folgenden Darreichungsformen von Nahrungsergänzung in eine Reihenfolge deiner Präferenz (1 = am liebsten, 8 = am wenigsten bevorzugt)", options: [
  "Kapseln - Praktisch und geschmacksneutral",
  "Loses Pulver - Flexibel dosierbar, gerne in Smoothies",
  "Gummies - Lecker und einfach",
  "Flüssig - Schnell verfügbar für den Körper",
  "Natürliche Lebensmittel - Fermentierte Produkte zum Essen",
  "Ist mir egal - Hauptsache wirksam",
  "Kombination - Verschiedene Formen je nach Bedarf",
  "Sachets - Sauber portioniert, kein Abmessen nötig"
] },
        { key: "6.3", type: "radio", text: "6.3 Wie viel gibst du monatlich für deine Gesundheit aus (Nahrungsergänzung, Vitamine, etc.)?", options: [
          "0€ - Ich kaufe normalerweise nichts",
          "1-25€ pro Monat",
          "26-50€ pro Monat",
          "51-100€ pro Monat",
          "101-200€ pro Monat",
          "Mehr als 200€ pro Monat"
        ] }
      ]
    },

    // 7 MARKENWAHRNEHMUNG & EINSCHÄTZUNG
    {
      title: "7. MARKENWAHRNEHMUNG & EINSCHÄTZUNG",
      subtitle: "Wie schätzt Du fairment ein?",
      questions: [
        // 7.0 Intro - fairment Konzept
        { key: "7.0", type: "info", text: "fairment Konzept",
          content: `Du willst deinen Tag mit mehr Energie und einem guten Bauchgefühl starten – etwas tun, das wirklich dranbleibt. fairment macht Darmgesundheit zur unkomplizierten Gewohnheit: Unsere tägliche Wohlfühl Routine kombiniert sporenbasierte SuperMikroben® mit dem präbiotischen Präzisiotikum® – entwickelt, um dein Mikrobiom im Alltag zuverlässig zu unterstützen.

Einfache Anwendung, transparente Qualität – die sporenbasierten Stämme sind von Natur aus stabil und praktisch im Handling, ganz ohne Kühlkette. Laborgeprüft in Deutschland und bereits seit 2015 aus Überzeugung für ein starkes Mikrobiom, mit Know how aus der fairment Community.

Die Anwendung ist unkompliziert: 2 Kapseln SuperMikroben® und eine Portion Präzisiotikum® mit Wasser – mehr braucht es nicht. Du merkst, dass die Routine zu dir passt; wenn nicht, greift unsere 90 Tage Zufriedenheitsgarantie.

Neu dabei: SuperFlora mit der Triple Harmony Formel, ganzheitlich gedacht für Vaginalflora, Schleimhäute und Harnwege

fairment: Einfach beginnen, leicht dranbleiben für mehr Energie Tag für Tag.`
        },

        // 7.1 Konzept-Bewertungsfragen (Scale 1-5)
        { key: "7.1", type: "scale", text: "7.1 Bewertung des Fairment-Konzepts (1 = stimme nicht zu, 5 = stimme voll zu)",
          rows: [
            "Ich würde dieses Produkt in den nächsten 30 Tagen ausprobieren",
            "Das Konzept passt zu meinen Bedürfnissen",
            "Die versprochenen Vorteile wirken glaubwürdig",
            "Das Konzept ist klar und leicht zu verstehen",
            "Das Konzept hebt sich positiv von anderen Drogerie-Produkten ab",
            "Die tägliche Anwendung wirkt für mich machbar",
            "fairment passt als Marke zu diesem Konzept",
            "Ich vertraue auf Qualität und Transparenz (laborgeprüft, seit 2015)",
            "Ich erwarte ein gutes Preis-Leistungs-Verhältnis",
            "Ich würde dieses Konzept weiterempfehlen"
          ],
          min: 1, max: 5, leftLabel: "1 = stimme nicht zu", rightLabel: "5 = stimme voll zu"
        },

        // 7.2 Produkt-Auswahl (ohne Bewertung)
        { key: "7.2", type: "product_selection", text: "7.2 Welche der folgenden Produkte interessieren dich?",
          description: "Du kannst ein oder mehrere Produkte auswählen.",
          required: true,
          products: [
            {
              id: "supermikroben",
              name: "SuperMikroben®",
              badge: "Bestseller Nr. 1",
              newBadge: "Neu",
              tagline: "Führendes sporenbasiertes Präparat (in DE)",
              rating: "(5645)",
              price: "34,90 €",
              pricePerKg: "232,67 €/kg",
              format: "60 Kapseln, 2 täglich zu einer Mahlzeit",
              imageUrl: superMikrobenImg,
              highlights: [
                "Eine Packung (60 Kapseln) reicht für einen Monat bei täglicher Einnahme von zwei Kapseln",
                "Erster vollwertiger Sporen-Biotikakomplex: Mit Bacillus subtilis (ausgezeichnet als Mikrobe des Jahres)",
                "Magensaftresistente (delayed-release) Kapseln: Schutz für die Bakterien – volle Entfaltung erst im Darm",
                "Jetzt neu mit 3-Säulen-Formel: Sporen-Biotika, B-Vitamin- und Mineralien-Komplex",
                "Mit Zink und Akazienfaser: Unterstützt das Immunsystem und nährt die Bakterienstämme",
                "10 Jahre Fermentationsgeschichte: Unsere gesamte Erfahrung in nur einer Kapsel"
              ],
              ingredients: [
                "Bacillus subtilis: 1,5x10⁹ KbE",
                "Bacillus coagulans: 9x10⁸ KbE",
                "Bacillus clausii: 6x10⁸ KbE",
                "Akazienfaser: 400mg",
                "Magnesium: 56,5mg",
                "Zink: 1,5mg"
              ],
              benefits: [
                "Zink trägt zu einer normalen Funktion des Immunsystems bei",
                "Magnesium trägt zur Verringerung von Müdigkeit und Ermüdung bei",
                "Vitamin B2 trägt zu einem normalen Energiestoffwechsel bei"
              ],
              questions: [
                "Die sporenbasierten Bakterienstämme in SuperMikroben® erscheinen mir wissenschaftlich fundiert.",
                "SuperMikroben® als täglich 2 Kapseln passt gut in meinen Alltag.",
                "Die Kombination aus 5 Mrd. KBE Probiotika, Akazienfasern und Zink überzeugt mich als ganzheitlicher Ansatz.",
                "Die Darstellung von SuperMikroben® als 'führendes sporenbasiertes Präparat' wirkt glaubwürdig auf mich.",
                "SuperMikroben® spricht mich als langfristige Lösung für meine Darmgesundheit an."
              ]
            },
            {
              id: "praezisiotikum",
              name: "Präzisiotikum®",
              badge: "Innovation",
              tagline: "Präbiotikum der 2. Generation",
              rating: "(3412)",
              price: "39,90€",
              pricePerKg: "266,00 €/kg",
              format: "150g Pulver, 5g täglich einrühren (30 Portionen)",
              imageUrl: praezisiotikumImg,
              highlights: [
                "Smart, Stark & Wirksam: Drei Oligosaccharide versorgen präzise nur gute Darmbakterien",
                "Klinisch nachgewiesen: Verdoppelt die Population guter Bakterien in einem Monat",
                "Unterstützt Darmschleimhaut und reibungslose Verdauung",
                "Fördert gesundes Immunsystem und stärkt den Stoffwechsel",
                "Geschmacksneutral - einfach in Getränke oder Speisen einrühren",
                "Gluten-, laktose- und sojafrei, ohne GVO, keine Farb- und Füllstoffe"
              ],
              ingredients: [
                "2'-Fucosyllactose (aus Kiwi)",
                "Lactulose",
                "Galactooligosaccharide (aus Milch)",
                "Xylo-Oligosaccharide (aus Mais)",
                "Vitamin B2 (Riboflavin)"
              ],
              benefits: [
                "Fructo-Oligosaccharide unterstützen Verdauung, Nährstoffaufnahme und Immunsystem",
                "Riboflavin (Vitamin B2) trägt zum Erhalt normaler Schleimhäute bei",
                "Perfekt in Kombination mit SuperMikroben® für symbiotischen Effekt"
              ],
              questions: [
                "Das Konzept der 'präzisen Fütterung nur der guten Darmbakterien' durch die drei Oligosaccharide macht für mich Sinn.",
                "Die Pulverform zum Einrühren in Getränke/Joghurt ist für mich praktischer als Kapseln.",
                "Die drei speziellen Oligosaccharide erscheinen mir als innovative Lösung.",
                "Präzisiotikum® als geschmacksneutrales Pulver mit 30 Portionen spricht mich an.",
                "Die wissenschaftliche Begründung als 'Präbiotikum der 2. Generation' überzeugt mich."
              ]
            },
            {
              id: "superflora",
              name: "SuperFlora®",
              badge: "Triple Harmony™",
              tagline: "Mit Triple Harmony™ Formel",
              rating: "(2847)",
              price: "29,90€",
              pricePerKg: "398,67 €/kg",
              format: "75g Pulver, 2,5g täglich (30 Portionen)",
              imageUrl: superFloraImg,
              highlights: [
                "Probiotische Lactobacillus-Stämme: Lactobacillus rhamnosus und Lactobacillus crispatus",
                "Zink und Biotin unterstützen Schutzbarrieren: Biotin trägt zur Erhaltung normaler Schleimhäute bei",
                "D-Mannose und Cranberry als bewährte tägliche Begleiter",
                "Triple Harmony™: Vaginalflora, Schleimhäute und Harnwege zusammen gedacht",
                "Speziell für Frauen und Mädchen ab 15 Jahren entwickelt",
                "Vegan, glutenfrei, laktosefrei, sojafrei und sorbitfrei"
              ],
              ingredients: [
                "Lactobacillus rhamnosus CA15",
                "Lactobacillus crispatus BC5",
                "D-Mannose",
                "Cranberry-Extrakt",
                "Inulin",
                "Bio-Akazienfaser",
                "Zink, Kupfer und Biotin"
              ],
              benefits: [
                "Biotin trägt zur Erhaltung normaler Schleimhäute bei (inkl. Vaginalschleimhaut)",
                "Zink trägt zur Erhaltung normaler Haut bei",
                "Zink trägt zu einer normalen Funktion des Immunsystems bei"
              ],
              questions: [
                "Die Triple Harmony™ Formel für Vaginalflora, Schleimhäute und Harnwege spricht mich als ganzheitlicher Ansatz an.",
                "SuperFlora® als spezifisches Produkt für das weibliche Mikrobiom ab 15 Jahren macht für mich Sinn.",
                "Die Kombination aus Lactobacillus-Stämmen, D-Mannose und Cranberry-Extrakt überzeugt mich.",
                "SuperFlora® als tägliche 2,5g Pulver-Routine für 30 Tage entspricht meinen Bedürfnissen.",
                "Die offene Thematisierung von Vaginalgesundheit, Ausfluss und Intimpflege gefällt mir."
              ]
            }
          ]
        },

        // 7.2a Produkt-Bewertung (nur wenn Produkte ausgewählt)
        { key: "7.2a", type: "product_rating_questions",
          text: "7.2a Bitte bewerte die ausgewählten Produkte",
          showWhen: { key: "7.2", hasSelection: true },
          sourceKey: "7.2"
        },

        // 7.3 Produktnutzung
        { key: "7.3", type: "radio", text: "7.3 Hast du Produkte von fairment bereits genutzt?",
          options: ["Nein", "Ja"]
        },
        { key: "7.3a", type: "textarea", showWhen: { key: "7.3", equals: "Ja" },
          text: "7.3a Wenn ja, welche Produkte hast Du von fairment genutzt?"
        },

        // 7.4 Produktkenntnis (Matrix) - nur anzeigen wenn User "Ja" bei 7.3 ausgewählt hat
        { key: "7.4", type: "matrix", 
          text: "7.4 Welche dieser Produkte kennst Du, oder hast Du genutzt?",
          showWhen: { key: "7.3", equals: "Ja" },
          rows: [
            "SuperMikroben® - Premium Biotika",
            "EffektivEnzyme® Premium Enzym-Komplex",
            "Präzisiotikum® - Premium Ballaststoff-Komplex",
            "MegaMukosa® - Für deine innere Schutzschicht",
            "TummyGummies® – Premium Biotika für Kinder",
            "SuperFlora® - Premium Biotika für Frauen"
          ],
          cols: ["Kenne ich", "Nutze ich aktuell", "Habe ich genutzt"]
        }
      ]
    },

    // 8 WETTBEWERB
    {
      title: "8. WETTBEWERB",
      questions: [
        { key: "8.1", type: "matrix", text: "8.1 Uns interessiert, ob du auch andere Anbieter rund um das Thema Darmgesundheit kennst.", rows: [
          "Braineffect",
          "everydays",
          "HerOne",
          "Kijimea",
          "Mybacs",
          "Naturtreu",
          "Omni-Biotic",
          "Andere:"
        ], cols: ["Kenne ich", "Nutze ich aktuell", "Habe ich genutzt", "Kenne ich nicht"] },
        { key: "8.2", type: "checkbox", text: "8.2 Wo kaufst du diese anderen Produkte hauptsächlich? (Maximal 3 Antworten möglich)", options: [
          "Apotheke",
          "Drogerie (dm, Rossmann, etc.)",
          "Online direkt beim Hersteller",
          "Amazon / Online-Marktplätze",
          "Reformhaus / Bioladen",
          "Supermarkt",
          "Andere:"
        ] }
      ]
    },

    // 9 WAHRNEHMUNG & ZUFRIEDENHEIT
    {
      title: "9. FAIRMENT WAHRNEHMUNG, ZUFRIEDENHEIT & WEITEREMPFEHLUNG",
      questions: [
        { key: "9.1", type: "textarea", text: "9.1 Hast du noch einen Wunsch, etwas das dir auf dem Herzen liegt?" }
      ]
    }
  ]), []);

  // Hilfsfunktionen
  function getAllQuestions(chapters) {
    return chapters.flatMap(c =>
      c.questions ? c.questions.map(q => ({ ...q, chapter: c.title, subtitle: c.subtitle })) : []
    );
  }
  
function shouldShowQuestion(question, answers) {
  if (!question.showWhen) return true;

  // Spezialfall: Prüfe ob Produkte ausgewählt wurden (für 7.2a)
  if (question.showWhen.hasSelection) {
    const selectedProducts = answers[question.showWhen.key + "_selected"] || [];
    const noneSelected = answers[question.showWhen.key + "_none"] || false;
    // Zeige Frage nur, wenn Produkte ausgewählt wurden (nicht "Keines")
    return selectedProducts.length > 0 && !noneSelected;
  }

  // Spezialfall: Prüfe ob Antworten existieren (für 2.4a)
  if (question.showWhen.hasAnswers) {
    const prevAnswer = answers[question.showWhen.key];
    return Array.isArray(prevAnswer) && prevAnswer.length > 0;
  }

  // Normale showWhen Logik
  const prevAnswer = answers[question.showWhen.key];
  if (question.showWhen.equals) {
    if (Array.isArray(prevAnswer)) {
      return prevAnswer.includes(question.showWhen.equals);
    }
    return prevAnswer === question.showWhen.equals;
  }
  
  return true;
}

  const allQuestions = useMemo(() => getAllQuestions(chapters), [chapters]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [visitorNumber, setVisitorNumber] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [visitorError, setVisitorError] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [started, setStarted] = useState(false);
  const [screenedOut, setScreenedOut] = useState(false);
  const [screenoutHandled, setScreenoutHandled] = useState(false); // NEU: Verhindert doppelte Screenout-Verarbeitung
  const [panelId, setPanelId] = useState(null); // Panel-ID für Bilendi/respondi Integration
  const [visitorRegistered, setVisitorRegistered] = useState(false); // NEU: Tracking ob Visitor registriert wurde
  const [isRegistering, setIsRegistering] = useState(false); // NEU: Loading State während Registrierung
  const [quotaReached, setQuotaReached] = useState(false); // NEU: Quota-Limit erreicht
  const [quotaInfo, setQuotaInfo] = useState(null); // NEU: Quota-Informationen
  const [isCheckingQuota, setIsCheckingQuota] = useState(true); // NEU: Loading während Quota-Check
  const [visitedChapters, setVisitedChapters] = useState(new Set([0])); // Erstes Kapitel ist initial besucht
  const visibleQuestions = allQuestions.filter(q => shouldShowQuestion(q, answers));
  const question = visibleQuestions[current];
  const isMobile = useIsMobile(); // Mobile Detection für responsive Matrix-Darstellung

  // Screenout-Prüfung nach Antwortänderung
  useEffect(() => {
    // Prüfe ob die aktuelle Antwort eine Screenout-Bedingung erfüllt
    if (question && question.screenout && answers[question.key]) {
      const answer = answers[question.key];
      console.log('=== SCREENOUT CHECK ===');
      console.log('Frage:', question.key);
      console.log('Antwort:', answer);
      console.log('Screenout-Werte:', question.screenout);
      console.log('Ist Screenout?', question.screenout.includes(answer));
      console.log('Bereits behandelt?', screenoutHandled);
      
      if (question.screenout.includes(answer) && !screenoutHandled) {
        console.log('🔴 SCREENOUT ERKANNT!');
        setScreenedOut(true);
        setScreenoutHandled(true); // Verhindert mehrfache Ausführung
        
        // Async-Funktion für sequentielle Ausführung
        const handleScreenout = async () => {
          if (!sessionId) {
            console.error('❌ Keine Session-ID - kann Screenout nicht speichern');
            return;
          }
          
          try {
            const detailedReason = `${question.key} - ${answer}`;
            console.log('Screenout Grund:', detailedReason);
            console.log('Session-ID:', sessionId.substring(0, 8) + '...');
            console.log('Visitor-Number:', visitorNumber);
            
            // STEP 1: Visitor als screened_out markieren (mit await!)
            console.log('📝 STEP 1: Markiere Visitor als screened_out...');
            const markResult = await markVisitorScreenedOut(sessionId, detailedReason);
            
            if (markResult.success) {
              console.log('✅ Visitor als screened_out markiert:', markResult.data);
            } else {
              console.error('❌ Fehler beim Markieren:', markResult.error);
            }
            
            // STEP 2: Partielle Antworten speichern (unabhängig von STEP 1, mit await!)
            console.log('📝 STEP 2: Speichere partielle Antworten...');
            console.log('Anzahl Antworten:', Object.keys(answers).length);
            
            const submitResult = await submitPartialSurvey(
              answers,
              visibleQuestions,
              fragebogenType,
              sessionId,
              visitorNumber,
              panelId
            );
            
            if (submitResult.success) {
              console.log('✅ Partielle Antworten gespeichert:', submitResult);
            } else {
              console.error('❌ Fehler beim Speichern der partiellen Antworten:', submitResult.error);
            }
            
            console.log('✅ Screenout-Handling abgeschlossen');
            
          } catch (error) {
            console.error('❌ Screenout-Handling Fehler:', error);
          }
        };
        
        // Screenout-Handling ausführen
        handleScreenout();
        
        // Der ScreenoutScreen übernimmt den Countdown und Redirect
        if (panelId) {
          console.log('Panel-ID vorhanden - ScreenoutScreen wird Redirect nach 3 Sekunden durchführen');
        } else {
          console.log('Kein Panel-ID - kein Redirect');
        }
      } else if (screenoutHandled) {
        console.log('⏭️ Screenout bereits behandelt - überspringe');
      }
    }
  }, [answers, question, sessionId, panelId, visibleQuestions, fragebogenType, visitorNumber, screenoutHandled]);

  // Panel-ID und Session-ID erfassen beim App-Start (OHNE Visitor-Registrierung)
  useEffect(() => {
    console.log('=== APP START - INITIALIZATION ===');
    
    // 1. Panel-ID erfassen
    const id = initializePanelTracking();
    console.log('Panel-ID von initializePanelTracking:', id);
    if (id) {
      setPanelId(id);
      console.log('Panel-Modus aktiv - ID:', id.substring(0, 8) + '...');
    } else {
      console.log('Keine Panel-ID - normaler Modus');
    }
    
    // 2. Session-ID generieren (NICHT registrieren!)
    let sid = localStorage.getItem('survey_session_id');
    if (!sid) {
      sid = uuidv4();
      localStorage.setItem('survey_session_id', sid);
      console.log('Session-ID generiert:', sid.substring(0, 8) + '...');
    } else {
      console.log('Session-ID aus localStorage:', sid.substring(0, 8) + '...');
    }
    setSessionId(sid);
    
    // 3. Quota Check (nur für Panel-Teilnehmer)
    if (id) {
      setIsCheckingQuota(true);
      checkQuota(1200).then(result => {
        setQuotaInfo(result);
        setQuotaReached(result.quotaReached);
        setIsCheckingQuota(false);
        
        console.log('Quota Status:', result);
        
        if (result.quotaReached) {
          console.log('🚫 QUOTA ERREICHT - Teilnahme nicht mehr möglich');
        } else {
          console.log(`✅ Quota OK - Noch ${result.remaining} Plätze verfügbar`);
        }
      }).catch(err => {
        console.error('Quota Check Fehler:', err);
        setIsCheckingQuota(false);
        // Bei Fehler: Nicht blockieren (graceful degradation)
      });
    } else {
      setIsCheckingQuota(false);
    }
    
    console.log('✅ App initialized - KEIN Visitor-Eintrag erstellt (passiert erst beim Start-Button)');
  }, []);

  // Tracking für Navigation
  const answeredQuestionKeys = useMemo(() => new Set(Object.keys(answers)), [answers]);

  // Track besuchte Kapitel basierend auf der aktuellen Frage
  useEffect(() => {
    if (question && question.chapter) {
      const currentChapterIndex = chapters.findIndex(ch => ch.title === question.chapter);
      if (currentChapterIndex >= 0) {
        setVisitedChapters(prev => {
          const newSet = new Set(prev);
          newSet.add(currentChapterIndex);
          return newSet;
        });
      }
    }
  }, [question, chapters]);

  // Fortschritts-Berechnung (basierend auf aktueller Frage)
  const progressPercent = useMemo(() => {
    const totalQuestions = visibleQuestions.length;
    return Math.round(((current + 1) / totalQuestions) * 100);
  }, [current, visibleQuestions]);

  // Verbleibende Fragen berechnen
  const remainingQuestions = useMemo(() => {
    return visibleQuestions.length - current - 1;
  }, [current, visibleQuestions]);

  // Finde zu welchem Kapitel zu springen ist
  const handleChapterClick = (chapterIndex) => {
    const targetChapter = chapters[chapterIndex];
    if (!targetChapter || !targetChapter.questions) return;

    // Finde die erste Frage des Kapitels in visibleQuestions
    const firstQuestionKey = targetChapter.questions[0]?.key;
    if (!firstQuestionKey) return;

    const targetIndex = visibleQuestions.findIndex(q => q.key === firstQuestionKey);
    if (targetIndex >= 0) {
      setCurrent(targetIndex);
    }
  };

  function handleChange(e, rowIdx) {
    const value = e.target.value;
    switch (question.type) {
      case "radio":
      case "select":
        setAnswers(a => ({ ...a, [question.key]: value }));
        break;
      case "checkbox":
        setAnswers(a => {
          const prev = a[question.key] || [];
          if (e.target.checked) {
            let limit = question.text.match(/maximal (\d+)/i);
            if (limit) {
              const max = parseInt(limit[1]);
              if (prev.length >= max) return a;
            }
            return { ...a, [question.key]: [...prev, value] };
          } else {
            return { ...a, [question.key]: prev.filter(v => v !== value) };
          }
        });
        break;
      case "text":
      case "textarea":
        setAnswers(a => ({ ...a, [question.key]: value }));
        break;
      case "matrix":
      case "scale":
        setAnswers(a => ({
          ...a,
          [question.key]: { ...(a[question.key] || {}), [rowIdx]: value },
        }));
        break;
      default:
        break;
    }
  }

  function prev() {
    if (current > 0) setCurrent(current - 1);
  }
  
  function next() {
    if (current < visibleQuestions.length - 1) {
      setCurrent(current + 1);
    }
  }

  // Check if current question is answered (all fields are required)
  function isCurrentQuestionAnswered() {
    // Alle Felder sind jetzt Pflichtfelder
    const answer = answers[question.key];

    // WICHTIG: Optionale Fragen ZUERST prüfen (vor den allgemeinen Checks)
    // Fragen 2.1 und 7.4 sind komplett optional
    if (question.key === '2.1' || question.key === '7.4') {
      return true; // Immer valide, auch ohne Antwort
    }

    // Spezielle Validierung für product_selection (7.2)
    if (question.type === "product_selection") {
      const selectedProducts = answers[question.key + "_selected"] || [];
      const noneSelected = answers[question.key + "_none"] || false;
      return selectedProducts.length > 0 || noneSelected;
    }

    // Spezielle Validierung für product_rating_questions (7.2a)
    if (question.type === "product_rating_questions") {
      // Hole die ausgewählten Produkte aus der Quelle (7.2)
      const sourceQuestion = chapters
        .flatMap(ch => ch.questions)
        .find(sq => sq.key === question.sourceKey);

      if (!sourceQuestion) return true;

      const selectedProductIds = answers[question.sourceKey + "_selected"] || [];

      // Wenn keine Produkte ausgewählt, ist die Frage automatisch beantwortet (wird übersprungen)
      if (selectedProductIds.length === 0) return true;

      // Prüfe ob alle Bewertungsfragen für alle ausgewählten Produkte beantwortet wurden
      const selectedProducts = sourceQuestion.products.filter(p =>
        selectedProductIds.includes(p.id)
      );

      for (const product of selectedProducts) {
        for (let qIdx = 0; qIdx < product.questions.length; qIdx++) {
          const answerKey = `${question.key}_${product.id}_q${qIdx}`;
          if (!answers[answerKey]) return false;
        }
      }

      return true;
    }

    // Info-Boxen benötigen keine Antwort (z.B. Frage 7.0)
    if (question.type === 'info') {
      return true;
    }
    
    // Spezielle Validierung für PLZ (1.7) - freiwillig, aber wenn ausgefüllt: 5 Ziffern
    if (question.key === '1.7') {
      // Freiwillig - leere Antwort ist OK
      if (!answer || answer === '') return true;
      // Wenn ausgefüllt, muss es 5 Ziffern sein
      return /^[0-9]{5}$/.test(answer);
    }
    
    // Spezielle Checks für Scale-Fragen mit Checkbox-Alternative (wie 2.3)
    if (question.type === 'scale' && question.checkboxRow) {
      // Bei Scale mit Checkbox: Entweder Checkbox ODER alle Scale-Zeilen müssen ausgefüllt sein
      const checkboxAnswer = answers[question.key + "_checkbox"];
      
      // Wenn Checkbox ausgewählt ist, ist die Frage vollständig beantwortet
      if (checkboxAnswer === true) {
        return true;
      }
      
      // Wenn Checkbox nicht ausgewählt, müssen alle Scale-Zeilen beantwortet sein
      if (!answer || typeof answer !== 'object') return false;
      const expectedRows = question.rows.length;
      const answeredRows = Object.keys(answer).length;
      return answeredRows === expectedRows;
    }
    
    // Check verschiedene Antworttypen
    if (answer === undefined || answer === null || answer === '') return false;
    if (Array.isArray(answer) && answer.length === 0) return false;
    
    if (question.type === 'matrix' || question.type === 'scale') {
      // Spezialfall: Fragen 2.1 und 7.4 sind komplett optional (User muss nichts auswählen)
      if (question.key === '2.1' || question.key === '7.4') {
        return true; // Immer valide, auch wenn leer
      }
      
      // Bei Matrix/Scale müssen alle Zeilen beantwortet sein (außer es gibt eine Checkbox-Alternative)
      if (!answer || typeof answer !== 'object') return false;
      
      // Bei Matrix-Fragen: "Andere:" Zeile ist optional
      if (question.type === 'matrix') {
        const requiredRows = question.rows.filter(row => !row.toLowerCase().includes('andere'));
        const answeredRows = Object.keys(answer).length;
        // Mindestens alle Pflichtzeilen müssen beantwortet sein
        return answeredRows >= requiredRows.length;
      }
      
      // Bei Scale: Alle Zeilen müssen beantwortet sein
      const expectedRows = question.rows.length;
      const answeredRows = Object.keys(answer).length;
      return answeredRows === expectedRows;
    }
    
    if (question.type === 'ranking') {
      // Bei Ranking müssen alle Optionen sortiert sein
      if (!Array.isArray(answer)) return false;
      return answer.length === question.options.length;
    }

    return true;
  }

  // Handle Survey Start - Registriert Visitor beim Button-Click
  async function handleStartSurvey() {
    console.log('=== SURVEY START ===');
    
    // Fallback: Wenn bereits registriert, nur starten
    if (visitorRegistered) {
      console.log('Visitor bereits registriert, starte Fragebogen');
      setStarted(true);
      return;
    }

    setIsRegistering(true);
    
    const MAX_RETRIES = 3;
    let attempt = 0;
    let registrationSuccess = false;
    
    while (attempt < MAX_RETRIES && !registrationSuccess) {
      attempt++;
      
      try {
        console.log(`Registriere Visitor... (Versuch ${attempt}/${MAX_RETRIES})`, {
          fragebogenType,
          panelId: panelId ? panelId.substring(0, 8) + '...' : 'none',
          sessionId: sessionId ? sessionId.substring(0, 8) + '...' : 'none'
        });
        
        // Register visitor with existing session ID
        const result = await registerVisitor(fragebogenType, panelId, sessionId);
        
        if (result.success) {
          setVisitorNumber(result.visitorNumber);
          setSessionId(result.sessionId);
          setVisitorRegistered(true);
          registrationSuccess = true;
          
          console.log('✅ Visitor erfolgreich registriert:', {
            visitorNumber: result.visitorNumber,
            isNew: result.isNew,
            sessionId: result.sessionId.substring(0, 8) + '...',
            panelId: panelId ? panelId.substring(0, 8) + '...' : 'none'
          });
          
          // Starte Fragebogen
          setStarted(true);
          
        } else {
          console.error(`❌ Versuch ${attempt}/${MAX_RETRIES} fehlgeschlagen:`, result.error);
          setVisitorError(result.error);
          
          if (attempt < MAX_RETRIES) {
            console.log(`⏳ Warte 2 Sekunden vor erneutem Versuch...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            // Alle Versuche fehlgeschlagen
            console.error('❌ Alle Registrierungsversuche fehlgeschlagen');
            alert(
              'Die Registrierung ist fehlgeschlagen.\n\n' +
              'Bitte:\n' +
              '1. Laden Sie die Seite neu (F5)\n' +
              '2. Versuchen Sie es erneut\n' +
              '3. Falls das Problem weiterhin besteht, kontaktieren Sie den Support.\n\n' +
              'Technischer Fehler: ' + result.error
            );
            // Fragebogen wird NICHT gestartet
          }
        }
        
      } catch (error) {
        console.error(`❌ Versuch ${attempt}/${MAX_RETRIES} Exception:`, error);
        setVisitorError(error.message);
        
        if (attempt < MAX_RETRIES) {
          console.log(`⏳ Warte 2 Sekunden vor erneutem Versuch...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        } else {
          // Alle Versuche fehlgeschlagen
          console.error('❌ Alle Registrierungsversuche fehlgeschlagen (Exception)');
          alert(
            'Die Registrierung ist fehlgeschlagen.\n\n' +
            'Bitte:\n' +
            '1. Laden Sie die Seite neu (F5)\n' +
            '2. Versuchen Sie es erneut\n' +
            '3. Falls das Problem weiterhin besteht, kontaktieren Sie den Support.\n\n' +
            'Technischer Fehler: ' + error.message
          );
          // Fragebogen wird NICHT gestartet
        }
      }
    }
    
    setIsRegistering(false);
  }

  // Submit Survey Function (Updated for Supabase)
  async function handleSubmit(fragebogenType) {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      console.log('Submitting survey...', { 
        fragebogenType, 
        answersCount: Object.keys(answers).length,
        sessionId: sessionId?.substring(0, 8) + '...',
        visitorNumber
      });

      // Submit to Supabase with sessionId, visitorNumber and panelId
      const result = await submitSurveyWithValidation(
        answers,
        visibleQuestions,
        fragebogenType,
        sessionId,        // Pass sessionId from state
        visitorNumber,    // Pass visitorNumber from state
        panelId          // Pass panelId from state (für Panel-Provider)
      );

      if (result.success) {
        setSubmitStatus('success');
        setSubmitResult(result);
        console.log('Survey submitted successfully to Supabase:', result);

        // Show success screen after successful submission
        // Der SuccessScreen übernimmt den Countdown und Redirect
        setShowSuccessScreen(true);
        
        if (panelId) {
          console.log('Panel-ID vorhanden - SuccessScreen wird Redirect nach 3 Sekunden durchführen');
        }

      } else {
        setSubmitStatus('error');
        setSubmitResult(result);
        console.error('Survey submission failed:', result.error);
      }

    } catch (error) {
      setSubmitStatus('error');
      setSubmitResult({ error: error.message });
      console.error('Survey submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

function renderQuestion(q) {
  // Spezielle Behandlung für Fragen 3.1, 3.2 und 9.1 mit Audio
  if (q.key === "3.1" || q.key === "3.2" || q.key === "9.1") {
    return (
      <div>
        <textarea
          value={answers[q.key] || ""}
          onChange={e =>
            setAnswers(a => ({
              ...a,
              [q.key]: e.target.value
            }))
          }
          placeholder="Deine Antwort als Text oder nutze die Audio-Aufnahme unten"
          style={{ width: "100%", minHeight: 100, marginTop: 8 }}
        />
        <AudioAnswer
          currentText={answers[q.key] || ""}
          onChange={newText =>
            setAnswers(a => ({
              ...a,
              [q.key]: newText
            }))
          }
          questionKey={q.key}
        />
      </div>
    );
  }
if (
  q.key === "9.5" ||
  (q.type === "radio" &&
    Array.isArray(q.options) &&
    q.options.length === 11 &&
    q.options[1] === "1" &&
    q.options[10] === "10")
) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "18px",
      marginTop: "18px",
      marginBottom: "26px"
    }}>
      {q.options.map((opt, idx) => (
        <label key={opt} style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          fontWeight: answers[q.key] === opt ? 700 : 500,
          color: answers[q.key] === opt ? "#7c4dff" : "#333"
        }}>
          <input
            type="radio"
            name={q.key}
            value={opt}
            checked={answers[q.key] === opt}
            onChange={handleChange}
            style={{
              marginBottom: "6px",
              width: "20px",
              height: "20px",
              accentColor: "#7c4dff"
            }}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}
if (q.type === "radio") {
  return q.options.map((opt) => {
    // Hardcode "Andere"-Felder nur für spezifische Fragen
    const isOther = (q.key === "1.4" && opt === "Sonstiges:") ||
                    (q.key === "4.1" && opt === "Sonstiges:") ||
                    (q.key === "7.1" && opt === "Andere:") ||
                    (q.key === "7.6" && opt === "Andere:") ||
                    (q.key === "7.6a" && opt === "Andere:") ||
                    (q.key === "7.6c" && opt === "Andere:") ||
                    (q.key === "8.1" && opt === "Andere:") ||
                    (q.key === "8.2" && opt === "Andere:");
    return (
      <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ flex: 1 }}>
          <input
            type="radio"
            name={q.key}
            value={opt}
            checked={answers[q.key] === opt}
            onChange={handleChange}
          />
          {opt}
        </label>
        {isOther && answers[q.key] === opt && (
          <input
            className="other-text-input"
            type="text"
            style={{
              marginLeft: "18px",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "2px solid #e0e0e0",
              fontSize: "1rem",
              flex: 2,
              minWidth: "180px"
            }}
            value={answers[q.key + "_other"] || ""}
            placeholder="Bitte angeben"
            onChange={e =>
              setAnswers(a => ({
                ...a,
                [q.key + "_other"]: e.target.value
              }))
            }
          />
        )}
      </div>
    );
  });
}
  
if (q.type === "checkbox") {
  const optionsToShow = q.options;

  return (
    <div>
      {/* Extra-Bereich für 2.4a: Zeige die Auswahlen aus 2.4 falls showSelected24=true */}
      {q.showSelected24 && answers["2.4"] && Array.isArray(answers["2.4"]) && answers["2.4"].length > 0 && (
        <div style={{
          background: 'rgba(104,69,165,0.08)',
          padding: '16px',
          borderRadius: '10px',
          marginBottom: '24px',
          borderLeft: '3px solid var(--fairment-purple)'
        }}>
          <strong>Deine ausgewählten primären Ziele aus 2.4:</strong>
          <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
            {answers["2.4"].map(opt => (
              <li key={opt} style={{ marginBottom: '4px' }}>{opt}</li>
            ))}
          </ul>
        </div>
      )}

      {optionsToShow.map((opt) => {
        // Hardcode "Andere"-Felder nur für spezifische Fragen
        const isOther = (q.key === "1.4" && opt === "Sonstiges:") ||
                        (q.key === "4.1" && opt === "Sonstiges:") ||
                        (q.key === "7.1" && opt === "Andere:") ||
                        (q.key === "7.5" && opt === "Andere:") ||
                        (q.key === "7.6a" && opt === "Andere:") ||
                        (q.key === "7.6c" && opt === "Andere:") ||
                        (q.key === "8.1" && opt === "Andere:") ||
                        (q.key === "8.2" && opt === "Andere:");
        const checked = Array.isArray(answers[q.key]) && answers[q.key].includes(opt);
        return (
          <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ flex: 1 }}>
              <input
                type="checkbox"
                name={q.key}
                value={opt}
                checked={checked}
                onChange={handleChange}
              />
              {opt}
            </label>
            {isOther && checked && (
              <input
                className="other-text-input"
                type="text"
                style={{
                  marginLeft: "18px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "2px solid #e0e0e0",
                  fontSize: "1rem",
                  flex: 2,
                  minWidth: "180px"
                }}
                value={answers[q.key + "_other"] || ""}
                placeholder="Bitte angeben"
                onChange={e =>
                  setAnswers(a => ({
                    ...a,
                    [q.key + "_other"]: e.target.value
                  }))
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
  
  if (q.type === "select") {
    return (
      <select
        value={answers[q.key] || ""}
        onChange={handleChange}
        style={{
          width: "100%",
          padding: "12px 16px",
          fontSize: "1rem",
          border: "2px solid #e0e0e0",
          borderRadius: "8px",
          background: "white",
          cursor: "pointer"
        }}
      >
        <option value="">-- Bitte wählen --</option>
        {q.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  
  if (q.type === "text") {
    // Spezielle Behandlung für PLZ-Feld (1.7) - nur Zahlen erlauben, freiwillig
    if (q.key === "1.7") {
      return (
        <input
          type="text"
          value={answers[q.key] || ""}
          onChange={(e) => {
            // Nur Zahlen erlauben
            const value = e.target.value.replace(/[^0-9]/g, '');
            // Maximal 5 Stellen für deutsche PLZ
            if (value.length <= 5) {
              handleChange({ target: { value, name: q.key } });
            }
          }}
          placeholder="12345 (optional)"
          maxLength="5"
          pattern="[0-9]{5}"
          inputMode="numeric"
        />
      );
    }
    
    return (
      <input
        type="text"
        value={answers[q.key] || ""}
        onChange={handleChange}
      />
    );
  }
  
  if (q.type === "textarea") {
    return (
      <textarea
        value={answers[q.key] || ""}
        onChange={handleChange}
        style={{ width: "100%", minHeight: 100 }}
      />
    );
  }
  
if (q.type === "matrix") {
    // Mobile: Card-basierte Darstellung
    if (isMobile) {
      return (
        <div className="matrix-cards">
          {q.rows.map((row, rIdx) => {
            const currentValue = answers[q.key]?.[rIdx];
            const isAndereRow = row.toLowerCase().includes("andere");
            
            return (
              <div key={rIdx} className={`matrix-card ${currentValue ? 'has-answer' : ''}`}>
                <div className="matrix-card-label">
                  {row}
                </div>
                
                <div className="matrix-card-options">
                  {q.cols.map((col) => {
                    const isPaul = row.includes("Paul – Gründer von fairment");
                    const isInfluencer = row.includes("Influencer-Kooperationen");
                    const isSperreCol = col === "Nutze ich aktuell" || col === "Habe ich genutzt";
                    const isDisabled = (isPaul || isInfluencer) && isSperreCol;
                    
                    return (
                      <label 
                        key={col} 
                        className={`matrix-card-option ${currentValue === col ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`${q.key}_${rIdx}`}
                          value={col}
                          checked={currentValue === col}
                          onChange={e => handleChange({ 
                            target: { value: col, checked: true, name: q.key } 
                          }, rIdx)}
                          disabled={isDisabled}
                        />
                        <span className="matrix-card-option-text">{col}</span>
                      </label>
                    );
                  })}
                </div>
                
                {isAndereRow && currentValue && (
                  <input
                    className="other-text-input matrix-card-other"
                    type="text"
                    placeholder="Bitte angeben..."
                    value={answers[`${q.key}_${rIdx}_other`] || ""}
                    onChange={e =>
                      setAnswers(a => ({
                        ...a,
                        [`${q.key}_${rIdx}_other`]: e.target.value
                      }))
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }
    
    // Desktop/Tablet: Tabellen-Darstellung
    return (
      <div className="matrix-responsive" style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th></th>
              {q.cols.map(col => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {q.rows.map((row, rIdx) => {
              const isAndereRow = row.toLowerCase().includes("andere");
              const hasAndereSelection = answers[q.key] && answers[q.key][rIdx];
              
              return (
                <React.Fragment key={row}>
                  <tr>
                    <td>{row}</td>
                    {q.cols.map((col, cIdx) => {
                      const isPaul = row.includes("Paul – Gründer von fairment");
                      const isInfluencer = row.includes("Influencer-Kooperationen");
                      const isSperreCol = col === "Nutze ich aktuell" || col === "Habe ich genutzt";
                      const isDisabled = (isPaul || isInfluencer) && isSperreCol;

                      return (
                        <td key={col}>
                          <input
                            type="radio"
                            name={`${q.key}_${rIdx}`}
                            value={col}
                            checked={answers[q.key] && answers[q.key][rIdx] === col}
                            onChange={e => handleChange({ target: { value: col, checked: true, name: q.key } }, rIdx)}
                            disabled={isDisabled}
                          />
                        </td>
                      );
                    })}
                  </tr>
                  {isAndereRow && hasAndereSelection && (
                    <tr>
                      <td colSpan={q.cols.length + 1} style={{ padding: "8px 20px", background: "#f9f9ff" }}>
                        <input
                          className="other-text-input"
                          type="text"
                          placeholder="Bitte angeben..."
                          value={answers[`${q.key}_${rIdx}_other`] || ""}
                          onChange={e =>
                            setAnswers(a => ({
                              ...a,
                              [`${q.key}_${rIdx}_other`]: e.target.value
                            }))
                          }
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "2px solid #e0e0e0",
                            borderRadius: "8px",
                            fontSize: "1rem"
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  
  if (q.type === "scale") {
    const scaleValues = Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i);
    return (
      <div className="scale-cards">
        {q.rows.map((row, rIdx) => (
          <div className="scale-card-row" key={row}>
            <div className="scale-card-row-inner">
              <div className="scale-card-row-label">{row}</div>
              <div className="scale-card-radios">
                <div className="scale-card-row-label-left">{q.leftLabel}</div>
                <div className="scale-card-radio-group">
                  {scaleValues.map(val => (
                    <label key={val} className="scale-card-radio-label">
                      <input
                        type="radio"
                        name={`${q.key}_${rIdx}`}
                        value={val}
                        checked={answers[q.key] && answers[q.key][rIdx] == val}
                        onChange={e => handleChange({ target: { value: val, checked: true, name: q.key } }, rIdx)}
                      />
                      <span className="scale-card-radio-custom">{val}</span>
                    </label>
                  ))}
                </div>
                <div className="scale-card-row-label-right">{q.rightLabel}</div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Spezielle Checkbox-Zeile */}
        {q.checkboxRow && (
          <div className="scale-card-row" style={{ 
            background: '#f9f9ff',
            borderTop: '2px solid #e0e0e0',
            marginTop: '16px'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              margin: 0, 
              padding: '16px',
              cursor: 'pointer',
              fontSize: '1.05rem'
            }}>
              <input
                type="checkbox"
                checked={answers[q.key + "_checkbox"] || false}
                onChange={e => setAnswers(a => ({
                  ...a,
                  [q.key + "_checkbox"]: e.target.checked
                }))}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: 500, color: '#333' }}>{q.checkboxRow}</span>
            </label>
          </div>
        )}
      </div>
    );
  }

if (q.type === "ranking") {
  return (
    <RankingQuestion
      question={q}
      answers={answers}
      setAnswers={setAnswers}
    />
  );
}

// Info-Box (für 7.0 - fairment Konzept)
if (q.type === "info") {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
      borderLeft: '4px solid #43a047',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(67, 160, 71, 0.2)'
    }}>
      <h3 style={{
        fontWeight: 'bold',
        fontSize: '1.5rem',
        marginBottom: '16px',
        color: '#2e7d32'
      }}>
        {q.text}
      </h3>
      <div style={{
        fontSize: '1rem',
        lineHeight: '1.7',
        color: '#424242',
        whiteSpace: 'pre-line'
      }}>
        {q.content}
      </div>
    </div>
  );
}

// Product Selection (für 7.2 - Nur Produktauswahl, horizontal)
if (q.type === "product_selection") {
  const selectedProducts = answers[q.key + "_selected"] || [];
  const noneSelected = answers[q.key + "_none"] || false;

  return (
    <div>
      <div style={{
        background: '#e3f2fd',
        borderLeft: '4px solid #1976d2',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <p style={{ marginBottom: 0 }}><strong>Hinweis:</strong> {q.description}</p>
      </div>

      {/* Produkt-Karten horizontal */}
      <div
        className="product-selection-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px',
          marginBottom: '24px',
          alignItems: 'start'
        }}
      >
        {q.products.map(product => {
          const isSelected = selectedProducts.includes(product.id);
          const isDisabled = noneSelected;
          const isExpanded = expandedProduct === product.id;

          return (
            <div
              className="product-card"
              key={product.id}
              style={{
                border: isSelected ? '3px solid #43a047' : '2px solid #e0e0e0',
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: isSelected ? '0 4px 16px rgba(67, 160, 71, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                opacity: isDisabled ? 0.5 : 1,
                background: isSelected ? '#e8f5e9' : 'white',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Auswahlhaken */}
              {isSelected && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#43a047',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(67, 160, 71, 0.4)'
                }}>
                  ✓
                </div>
              )}

              {/* Klickbarer Bereich für Auswahl */}
              <div
                className="product-card-clickable"
                onClick={() => {
                  if (isDisabled) return;
                  const newSelected = isSelected
                    ? selectedProducts.filter(id => id !== product.id)
                    : [...selectedProducts, product.id];
                  setAnswers(a => ({
                    ...a,
                    [q.key + "_selected"]: newSelected,
                    [q.key + "_none"]: false
                  }));
                }}
                style={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  minHeight: '480px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Produktbild */}
                {product.imageUrl && (
                  <div className="product-card-image" style={{
                    textAlign: 'center',
                    padding: '24px 24px 16px',
                    background: 'white',
                    minHeight: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      style={{
                        maxHeight: '180px',
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}

                {/* Produktinfo */}
                <div style={{ padding: '16px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: 8 }}>
                    <h4 style={{ fontSize: '1.2rem', fontWeight: 'bold', margin: '0 0 8px 0', color: '#333' }}>
                      {product.name}
                    </h4>
                    {product.badge && (
                      <span style={{
                        background: '#ffd54f',
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        marginRight: '6px'
                      }}>
                        {product.badge}
                      </span>
                    )}
                    {product.newBadge && (
                      <span style={{
                        background: '#43a047',
                        color: 'white',
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        display: 'inline-block'
                      }}>
                        {product.newBadge}
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: 8, lineHeight: '1.4' }}>
                    {product.tagline}
                  </p>
                  <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#333', marginBottom: 4 }}>
                      {product.price}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>
                      {product.format}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Button */}
              <button
                className="product-card-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedProduct(isExpanded ? null : product.id);
                }}
                style={{
                  margin: '0 20px 20px',
                  padding: '12px',
                  background: isExpanded ? '#7c4dff' : '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isExpanded ? 'Details ausblenden ▲' : 'Details anzeigen ▼'}
              </button>

              {/* Accordion Details */}
              <div className="product-card-accordion" style={{
                maxHeight: isExpanded ? '2000px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-out',
                background: '#f9f9f9',
                borderTop: isExpanded ? '2px solid #e0e0e0' : 'none'
              }}>
                <div className="product-card-accordion-inner" style={{
                  padding: isExpanded ? '20px' : '0 20px',
                  opacity: isExpanded ? 1 : 0,
                  transition: 'opacity 0.2s ease-out, padding 0.3s ease-out'
                }}>
                  <h5 style={{ marginBottom: 16, fontWeight: 'bold', fontSize: '1.1rem' }}>Produktdetails</h5>

                  {/* Qualitätsmerkmale */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '8px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#43a047', fontSize: '0.85rem', fontWeight: 600 }}>
                      <span>✓</span> 100% natürliche Inhaltsstoffe
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#43a047', fontSize: '0.85rem', fontWeight: 600 }}>
                      <span>✓</span> GMP zertifiziert
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#43a047', fontSize: '0.85rem', fontWeight: 600 }}>
                      <span>✓</span> Made in Germany
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#43a047', fontSize: '0.85rem', fontWeight: 600 }}>
                      <span>✓</span> Laborgeprüft
                    </div>
                  </div>

                  {/* Highlights */}
                  {product.highlights && product.highlights.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h6 style={{ fontWeight: 'bold', marginBottom: 12, fontSize: '0.95rem', color: '#555' }}>
                        HIGHLIGHTS:
                      </h6>
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                        {product.highlights.map((h, i) => (
                          <li key={i} style={{
                            display: 'flex',
                            marginBottom: 8,
                            fontSize: '0.9rem',
                            lineHeight: '1.5'
                          }}>
                            <span style={{ color: '#43a047', marginRight: 8, fontWeight: 'bold' }}>●</span>
                            <span style={{ color: '#555' }}>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Inhaltsstoffe */}
                  {product.ingredients && product.ingredients.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h6 style={{ fontWeight: 'bold', marginBottom: 12, fontSize: '0.95rem', color: '#555' }}>
                        INHALTSSTOFFE:
                      </h6>
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                        {product.ingredients.map((ing, i) => (
                          <li key={i} style={{
                            display: 'flex',
                            marginBottom: 4,
                            fontSize: '0.85rem'
                          }}>
                            <span style={{ color: '#43a047', marginRight: 8 }}>✓</span>
                            <span style={{ color: '#666' }}>{ing}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Benefits */}
                  {product.benefits && product.benefits.length > 0 && (
                    <div style={{
                      padding: '16px',
                      background: 'white',
                      borderRadius: '8px',
                      borderLeft: '4px solid #43a047'
                    }}>
                      <p style={{ fontWeight: 'bold', marginBottom: 8, fontSize: '0.9rem', color: '#333' }}>
                        ZUGELASSENE WIRKUNG:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                        {product.benefits.map((b, i) => (
                          <li key={i} style={{
                            display: 'flex',
                            marginBottom: 6,
                            fontSize: '0.85rem',
                            color: '#555'
                          }}>
                            <span style={{ marginRight: 8 }}>•</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* "Keines interessiert mich" Option */}
      <div
        onClick={() => {
          setAnswers(a => ({
            ...a,
            [q.key + "_none"]: !noneSelected,
            [q.key + "_selected"]: noneSelected ? selectedProducts : []
          }));
        }}
        style={{
          border: noneSelected ? '3px solid #7c4dff' : '2px solid #e0e0e0',
          borderRadius: '12px',
          padding: '20px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          background: noneSelected ? '#f3e5f5' : 'white',
          boxShadow: noneSelected ? '0 4px 12px rgba(124, 77, 255, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: noneSelected ? '3px solid #7c4dff' : '2px solid #ccc',
            background: noneSelected ? '#7c4dff' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}>
            {noneSelected && '✓'}
          </div>
          <span style={{
            fontSize: '1rem',
            fontWeight: noneSelected ? 600 : 400,
            color: noneSelected ? '#7c4dff' : '#555'
          }}>
            Keines dieser Produkte interessiert mich
          </span>
        </div>
      </div>
    </div>
  );
}

// Product Rating Questions (für 7.2a - Bewertungsfragen für ausgewählte Produkte)
if (q.type === "product_rating_questions") {
  // Hole die ausgewählten Produkte aus 7.2
  const sourceQuestion = chapters
    .flatMap(ch => ch.questions)
    .find(sq => sq.key === q.sourceKey);

  if (!sourceQuestion) return null;

  const selectedProductIds = answers[q.sourceKey + "_selected"] || [];
  const selectedProducts = sourceQuestion.products.filter(p =>
    selectedProductIds.includes(p.id)
  );

  if (selectedProducts.length === 0) return null;

  return (
    <div>
      {selectedProducts.map((product, pIdx) => (
        <div key={product.id} style={{
          marginBottom: pIdx < selectedProducts.length - 1 ? '40px' : 0,
          padding: '24px',
          background: '#f9f9f9',
          borderRadius: '12px',
          border: '2px solid #e0e0e0'
        }}>
          <h4 style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            marginBottom: '20px',
            color: '#333',
            borderBottom: '3px solid #7c4dff',
            paddingBottom: '12px'
          }}>
            {product.name}
          </h4>

          <div className="scale-cards">
            {product.questions.map((pq, qIdx) => (
              <div className="scale-card-row" key={qIdx}>
                <div className="scale-card-row-inner">
                  <div className="scale-card-row-label">{pq}</div>
                  <div className="scale-card-radios">
                    <div className="scale-card-row-label-left">1 = stimme nicht zu</div>
                    <div className="scale-card-radio-group">
                      {[1, 2, 3, 4, 5].map(val => (
                        <label key={val} className="scale-card-radio-label">
                          <input
                            type="radio"
                            name={`${q.key}_${product.id}_q${qIdx}`}
                            value={val}
                            checked={answers[`${q.key}_${product.id}_q${qIdx}`] === val}
                            onChange={() => setAnswers(a => ({
                              ...a,
                              [`${q.key}_${product.id}_q${qIdx}`]: val
                            }))}
                          />
                          <span className="scale-card-radio-custom">{val}</span>
                        </label>
                      ))}
                    </div>
                    <div className="scale-card-row-label-right">5 = stimme voll zu</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

return <div>Unbekannter Fragetyp</div>;
}

  // Show Quota Full Screen if quota is reached (nur für Panel-Teilnehmer)
  if (quotaReached && panelId) {
    return (
      <QuotaFullScreen 
        panelId={panelId}
        onRedirect={() => redirectToPanel('quotasfull', panelId)}
        quotaInfo={quotaInfo}
      />
    );
  }

  // Show Loading während Quota Check (nur für Panel-Teilnehmer)
  if (isCheckingQuota && panelId) {
    return (
      <div className="question-bg">
        <header className="question-header">
          <img
            src={logoSrc}
            alt="Fairment Logo"
            className="question-bg__logo"
          />
        </header>
        <div className="glass-card-wrapper">
          <div className="glass-card" style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Einen Moment...</h2>
            <p>Prüfe Verfügbarkeit...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show Intro Card if not started yet
  if (!started) {
    return (
      <div className="question-bg">
        <header className="question-header">
          <img
            src={logoSrc}
            alt="Fairment Logo"
            className="question-bg__logo"
          />
        </header>
      <div className="glass-card-wrapper">
        <FairmentIntroCardB 
          onStart={handleStartSurvey}
          isLoading={isRegistering}
          quotaInfo={quotaInfo}
        />
      </div>
      </div>
    );
  }

  // Show Screenout Screen if user is screened out
  if (screenedOut) {
    return (
      <ScreenoutScreen 
        panelId={panelId}
        onRedirect={() => panelId && redirectToPanel('screenout', panelId)}
      />
    );
  }

  // Show Success Screen if survey is submitted successfully
  if (showSuccessScreen) {
    return (
      <SuccessScreen 
        panelId={panelId}
        onRedirect={() => panelId && redirectToPanel('complete', panelId)}
      />
    );
  }

  return (
    <div className="question-bg">
      <ChapterNavigation
        chapters={chapters}
        currentChapter={question?.chapter}
        onChapterClick={handleChapterClick}
        answeredQuestions={answeredQuestionKeys}
        visitedChapters={visitedChapters}
      />
      <header className="question-header">
        <img
          src={logoSrc}
          alt="Fairment Logo"
          className="question-bg__logo"
        />
      </header>
      <div className="glass-card-wrapper">
        <div className="glass-card">
          <h2>{question.chapter}</h2>
        {question.subtitle && (
          <div className="subtitle">
            {question.subtitle}
          </div>
        )}
        <div className="progress">
          Frage {current + 1} von {visibleQuestions.length}
        </div>
        <div className="question-content">
          <div className="question-text">
            {question.text}
          </div>
          <div className="answer-area">{renderQuestion(question)}</div>
        </div>
        <div className="glass-btn-row">
          <button
            className={current === visibleQuestions.length - 1 ? 'glass-btn-row-back-last' : ''}
            onClick={prev}
            disabled={current === 0 || isSubmitting}
          >
            Zurück
          </button>
          
          {current === visibleQuestions.length - 1 ? (
            // Submit Button bei letzter Frage
            <button
              className="glass-btn-row-submit"
              onClick={() => handleSubmit(fragebogenType)}
              disabled={isSubmitting || !isCurrentQuestionAnswered()}
              style={{
                background: isSubmitting || !isCurrentQuestionAnswered() ? '#999' : '#28a745',
                color: 'white',
                fontWeight: 'bold',
                cursor: !isCurrentQuestionAnswered() ? 'not-allowed' : 'pointer',
              }}
              title={!isCurrentQuestionAnswered() ? 'Bitte beantworte die Pflichtfrage' : ''}
            >
              {isSubmitting ? 'Übertrage...' : 'Fragebogen abschließen'}
            </button>
          ) : (
            // Weiter Button
            <button
              onClick={next}
              disabled={isSubmitting || !isCurrentQuestionAnswered()}
              style={{
                cursor: !isCurrentQuestionAnswered() ? 'not-allowed' : 'pointer',
                opacity: !isCurrentQuestionAnswered() ? 0.6 : 1
              }}
              title={!isCurrentQuestionAnswered() ? 'Bitte beantworte die Pflichtfrage' : ''}
            >
              Weiter
            </button>
          )}
        </div>
        
        {/* Submit Status Display */}
        {submitStatus && (
          <div style={{
            marginTop: 20,
            padding: 15,
            borderRadius: 8,
            textAlign: 'center',
            background: submitStatus === 'success' ? '#d4edda' : '#f8d7da',
            color: submitStatus === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${submitStatus === 'success' ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {submitStatus === 'success' ? (
              <div>
                <div style={{ fontSize: '1.2em', marginBottom: 8 }}>✅ Fragebogen erfolgreich übertragen!</div>
                <div>Danke für deine Teilnahme.</div>
                {submitResult?.submissionId && (
                  <div style={{ fontSize: '0.9em', marginTop: 8, color: '#6c757d' }}>
                    ID: {submitResult.submissionId}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '1.2em', marginBottom: 8 }}>❌ Übertragung fehlgeschlagen</div>
                <div>Bitte versuche es erneut oder kontaktiere den Support.</div>
                {submitResult?.error && (
                  <div style={{ fontSize: '0.9em', marginTop: 8 }}>
                    Fehler: {submitResult.error}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}